import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NotificationBox } from '../../core/ui/notification-box/notification-box';
import { AccountService } from '../../services/account-service';
import { TextboxModule } from '../../../../../../libs/Shared/components/inputs/textbox/textbox.module';
import { environment } from '../../../environments/environment';

export type DocNavItem = {
  id: string;
  label: string;
};

export type DocNavGroup = {
  id: string;
  label: string;
  items: DocNavItem[];
};

@Component({
  selector: 'app-documentation-page',
  imports: [FormsModule, CommonModule, NotificationBox, TextboxModule],
  templateUrl: './documentation-page.html',
  styleUrl: './documentation-page.scss',
})
export class DocumentationPage implements OnInit, OnDestroy {

  sideNavVisible = signal<boolean>(true);
  readonly apiDocsUrl: SafeResourceUrl;
  readonly auditDocsUrl: SafeResourceUrl;
  readonly aiToolsDocsUrl: SafeResourceUrl;

  readonly navGroups: DocNavGroup[] = [
    {
      id: 'API',
      label: 'API',
      items: [
        { id: 'api-overview', label: 'Overview' }
      ],
    },
    {
      id: 'Integrations',
      label: 'Integrations',
      items: [
        { id: 'audit', label: 'Audit' },
        { id: 'swipesum-connect', label: 'Swipesum Connect' },
        { id: 'campfire', label: 'Campfire' },
        { id: 'ai-tools', label: 'AI Tools' } // we should leave this one last, it is only for ai assistant tools
      ],
    },
  ];

  readonly activeItemId = signal<string>('api-overview');
  readonly expandedGroups = signal<Set<string>>(new Set(this.navGroups.map(g => g.id)));
  readonly mobileNavOpen = signal<boolean>(false);

  private readonly flatItems: (DocNavItem & { groupLabel: string })[] = this.navGroups.flatMap(g =>
    g.items.map(i => ({ ...i, groupLabel: g.label }))
  );

  readonly activeItem = computed(() => this.flatItems.find(i => i.id === this.activeItemId()) ?? this.flatItems[0]);
  readonly previousItem = computed(() => {
    const idx = this.flatItems.findIndex(i => i.id === this.activeItemId());
    return idx > 0 ? this.flatItems[idx - 1] : null;
  });
  readonly nextItem = computed(() => {
    const idx = this.flatItems.findIndex(i => i.id === this.activeItemId());
    return idx >= 0 && idx < this.flatItems.length - 1 ? this.flatItems[idx + 1] : null;
  });

  constructor(
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer
  ) {
    this.apiDocsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.apiDocsUrl);
    this.auditDocsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.auditDocsUrl);
    this.aiToolsDocsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.aiToolsUrl);
  }

  ngOnInit() {
    const fragment = window.location.hash.replace('#', '');
    if (fragment && this.flatItems.some(i => i.id === fragment)) {
      this.selectItem(fragment);
    }
  }

  ngOnDestroy() {
  }

  selectItem(id: string) {
    this.activeItemId.set(id);
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${id}`);
    window.scrollTo({ top: 0 });
  }

  toggleSideNav() {
    this.sideNavVisible.update(v => !v);
  }

  toggleGroup(id: string) {
    const next = new Set(this.expandedGroups());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.expandedGroups.set(next);
  }

  isExpanded(id: string): boolean {
    return this.expandedGroups().has(id);
  }
}
